import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { GET_WEEKLY_AUDIT_QUEUE, REVIEW_INVENTORY_AUDIT } from "./graphql";

const FILL_LABELS = {
  FULL: "Full",
  MORE_THAN_HALF: "More than half",
  ABOUT_HALF: "About half",
  LESS_THAN_HALF: "Less than half",
  EMPTY: "Empty",
};

const WeeklyAuditQueue = () => {
  const [reviewing, setReviewing] = useState(null); // { auditId, action }
  const [dismissReason, setDismissReason] = useState("QUESTIONED");
  const [adminNote, setAdminNote] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_WEEKLY_AUDIT_QUEUE, {
    fetchPolicy: "network-only",
  });

  const [reviewAudit, { loading: reviewLoading }] = useMutation(REVIEW_INVENTORY_AUDIT, {
    onCompleted: () => {
      setReviewing(null);
      setAdminNote("");
      refetch();
    },
  });

  const flagged = data?.getWeeklyAuditQueue?.flagged || [];
  const delinquencies = data?.getWeeklyAuditQueue?.delinquencies || [];

  const submitReview = () => {
    if (!reviewing) return;
    reviewAudit({
      variables: {
        data: {
          auditId: reviewing.auditId,
          action: reviewing.action,
          dismissReason: reviewing.action === "DISMISS" ? dismissReason : null,
          adminNote: adminNote || null,
        },
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 text-sm">
          Weekly Blind Audit - Needs Review
        </h3>
        <p className="text-xs text-gray-500 mt-1 max-w-xl">
          Each row is a category where the employee's blind physical count (no system
          number was ever shown to them) didn't match what the system expects. Approving
          adjusts the employee's recorded usage to match what they reported; dismissing
          leaves the system's number as-is.
        </p>

        {loading && <p className="text-sm text-gray-500 mt-3">Loading…</p>}
        {error && <p className="text-sm text-red-600 mt-3">{error.message}</p>}
        {!loading && !error && flagged.length === 0 && (
          <p className="text-sm text-gray-500 mt-3">Nothing flagged right now.</p>
        )}
        {!loading && flagged.length > 0 && (
          <div className="overflow-x-auto mt-3">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Category</th>
                  <th className="px-4 py-2">Cycle</th>
                  <th className="px-4 py-2">System Said</th>
                  <th className="px-4 py-2">Employee Reported</th>
                  <th className="px-4 py-2">Diff</th>
                  <th className="px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {flagged.map((row) => (
                  <tr key={row.id}>
                    <td className="px-4 py-2 font-medium">{row.employeeName}</td>
                    <td className="px-4 py-2">{row.categoryName}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{row.auditCycleDate}</td>
                    <td className="px-4 py-2">{row.systemExpectedQty}</td>
                    <td className="px-4 py-2">
                      {row.employeeReportedQty}
                      {row.employeeReportedFillEstimate && (
                        <span className="block text-[10px] text-gray-500">
                          Open unit: {FILL_LABELS[row.employeeReportedFillEstimate] || row.employeeReportedFillEstimate}
                        </span>
                      )}
                    </td>
                    <td
                      className={`px-4 py-2 font-semibold ${
                        row.diffQty < 0 ? "text-red-600" : "text-amber-600"
                      }`}
                    >
                      {/* diffQty = employee's reported count - system's expected count.
                          Negative = employee has less than expected (shrinkage, the more
                          concerning case); positive = employee has more (likely an
                          under-deduction somewhere). */}
                      {row.diffQty > 0 ? `+${row.diffQty}` : row.diffQty}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="px-2 py-1 rounded text-xs font-semibold bg-green-600 text-white"
                          onClick={() => setReviewing({ auditId: row.id, action: "APPROVE" })}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          className="px-2 py-1 rounded text-xs font-semibold bg-gray-200 text-gray-800"
                          onClick={() => setReviewing({ auditId: row.id, action: "DISMISS" })}
                        >
                          Dismiss
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 text-sm">Still to Audit</h3>
        <p className="text-xs text-gray-500 mt-1">
          Employees with at least one category not yet counted this cycle. No automatic
          escalation happens from this - it's visibility only.
        </p>
        {!loading && delinquencies.length === 0 && (
          <p className="text-sm text-gray-500 mt-3">Everyone is caught up.</p>
        )}
        {!loading && delinquencies.length > 0 && (
          <div className="overflow-x-auto mt-3">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Pending Categories</th>
                  <th className="px-4 py-2">Oldest Pending Since</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {delinquencies.map((d) => (
                  <tr key={d.employeeId}>
                    <td className="px-4 py-2 font-medium">{d.employeeName}</td>
                    <td className="px-4 py-2">{d.pendingCount}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{d.oldestPendingDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {reviewing && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="absolute inset-0 bg-black/50" onClick={() => setReviewing(null)} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-xl shadow-xl p-5">
            <h4 className="font-semibold text-gray-900 mb-3">
              {reviewing.action === "APPROVE"
                ? "Approve - adjust inventory to match employee's count"
                : "Dismiss - no inventory change"}
            </h4>
            {reviewing.action === "DISMISS" && (
              <div className="mb-3">
                <label className="block text-xs text-gray-500 mb-1">Reason</label>
                <select
                  value={dismissReason}
                  onChange={(e) => setDismissReason(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
                >
                  <option value="QUESTIONED">Questioned - following up</option>
                  <option value="REJECTED">Rejected - don't believe the count</option>
                </select>
              </div>
            )}
            <div className="mb-4">
              <label className="block text-xs text-gray-500 mb-1">Note (optional)</label>
              <textarea
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 rounded-lg text-sm border border-gray-300"
                onClick={() => setReviewing(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={reviewLoading}
                onClick={submitReview}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-blue-600 text-white disabled:opacity-50"
              >
                {reviewLoading ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeeklyAuditQueue;
